import { useEffect, useState } from "react";
import { FileText } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { api } from "@/lib/api";
import type { DocumentSummary } from "@/types/api";
import { formatBytes, formatRelativeTime } from "@/lib/utils";

export function DocsPage() {
  const [docs, setDocs] = useState<DocumentSummary[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    api
      .listDocuments()
      .then((r) => !cancelled && setDocs(r.documents))
      .catch((err) => !cancelled && setError((err as Error).message))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="mx-auto h-full max-w-2xl overflow-y-auto p-4">
      {loading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : error ? (
        <p className="text-sm text-destructive">Error: {error}</p>
      ) : docs.length === 0 ? (
        <p className="text-sm text-muted-foreground">No documents yet. Upload one to get started.</p>
      ) : (
        <ul className="flex flex-col gap-2">
          {docs.map((d) => (
            <li key={d.id} className="flex items-start gap-3 rounded-lg border bg-card p-3">
              <FileText className="mt-0.5 h-5 w-5 shrink-0 text-muted-foreground" />
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-medium">{d.title ?? d.originalName}</div>
                <div className="text-xs text-muted-foreground">
                  {d.category ?? "uncategorized"} · {formatBytes(d.sizeBytes)} ·{" "}
                  {formatRelativeTime(d.uploadedAt)}
                </div>
                {d.summary ? (
                  <p className="mt-1 line-clamp-2 text-xs text-muted-foreground">{d.summary}</p>
                ) : null}
              </div>
              <StatusBadge status={d.status} />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function StatusBadge({ status }: { status: DocumentSummary["status"] }) {
  const variant =
    status === "indexed"
      ? "default"
      : status === "error"
        ? "destructive"
        : "secondary";
  return <Badge variant={variant}>{status}</Badge>;
}
